import React from 'react';
import { Button } from '../../../../../../ui';
import { UserAssessment } from '../../../../../../utils/types/assessment_types';
import { BrowserSettingsCard } from './BrowserSettingsCard';
import { CameraSetupCard } from './CameraSetupCard';
import { FinalSubmitCard } from './FinalSubmitCard';
import { ModernStepper } from './ModernStepper';
import { StartRecordingCard } from './StartRecordingCard';
import { StopRecordingCard } from './StopRecordingCard';
import { SystemSetupCard } from './SystemSetupCard';

type AssessmentInstructionsModalProps = {
  setModalContent: (content: number) => void;
  setIsPractice: (content: boolean) => void;
  userAssessmentList: UserAssessment[];
  afterHelpModalContent?: number; // where to go after instructions complete
};

export const AssessmentInstructionsModal = ({
  setModalContent,
  setIsPractice,
  userAssessmentList,
  afterHelpModalContent,
}: AssessmentInstructionsModalProps) => {
  const handlePractice = () => {
    setIsPractice(true);
    setModalContent(3);
  };

  const HELP_SEEN_KEY = 'hiacuity_help_seen';

  const handleStepperComplete = () => {
    // Mark as seen so subsequent visits skip the help
    localStorage.setItem(HELP_SEEN_KEY, 'true');
    setShowCompletion(true);
  };

  const handleContinue = () => {
    // afterHelpModalContent is set when coming from Start Assessment / Practice
    // Fall back to cam/mic step if not provided
    const target = afterHelpModalContent ?? 2;
    setModalContent(target);
  };

  const [showCompletion, setShowCompletion] = React.useState(false);

  const instructionSteps = [
    {
      id: 1,
      title: 'Allow Web Browser Access to Camera and Microphone',
      description:
        '<b>Step 1:</b> Please allow the Web Browser access to your camera and microphone. See example below for Google Chrome - Click the <b>Allow</b> button:',
      component: <BrowserSettingsCard />,
    },
    {
      id: 2,
      title: 'System Setup: Camera & Microphone',
      description:
        '<b>Step 2:</b> Configure your camera and microphone for the assessment. Select your preferred devices below:',
      component: <CameraSetupCard />,
    },
    {
      id: 3,
      title: 'System Setup: Assessment Overview',
      description:
        '<b>Step 3:</b> Review your assessment details below — including total points and time allowed — then click <b>Next</b> when you are ready to begin:',
      component: <SystemSetupCard />,
    },
    {
      id: 4,
      title: 'Press Start Recording and speak your answer',
      description:
        '<b>Step 4:</b> Read the question, then press the <b>Start Recording</b> button and speak your answer:',
      component: <StartRecordingCard />,
    },
    {
      id: 5,
      title: 'Press Stop Recording when you have finished',
      description:
        '<b>Step 5:</b> When you have finished speaking your answer, press the <b>Stop Recording</b> button. You can then review and redo before submitting:',
      component: <StopRecordingCard />,
    },
    {
      id: 6,
      title: 'Press Submit',
      description:
        '<b>Step 6:</b> Press <b>Submit</b> to move on. Please note, you can Redo the recording by pressing the Redo Recording button:',
      component: <FinalSubmitCard />,
    },
  ];

  if (showCompletion) {
    return (
      <div className='bg-gray-50 p-6 rounded-lg text-center'>
        <div className='mb-8'>
          <div className='w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4'>
            <svg
                className='w-10 h-10 text-white'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <h2 className='text-2xl font-bold text-gray-900 mb-2'>
            Instructions Complete!
          </h2>
          <p className='text-gray-600'>
            You&apos;re ready to start your assessment. Choose an option below:
          </p>
        </div>

        <div className='flex gap-4 justify-center'>
          <Button variant='primary' label='Continue' onClick={handleContinue} />
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white p-4'>
      { /* Modern Stepper */ }
      <ModernStepper
          steps={instructionSteps}
          onComplete={handleStepperComplete}
      />
    </div>
  );
};
